import React, { useState } from 'react';
import { Mail, Phone, Globe, MapPin, Pencil, Images, Play, Trash2, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { BioRichTextDisplay } from './BioMediaDisplay';
import { BrandIcons, getSocialLinks, getExtraLinks, extractYouTubeVideoId } from './BrandIcons';
import { GalleryGrid } from './GalleryLightbox';

const BioTemplateMinimal = ({ card, bioUrl, constructSocialUrl, trackLinkClick, onQRClick, hideQR = false, isOwner = false, onUpdateProfileImage, onUpdateLogo, isPremium = false }) => {
  const [isBioExpanded, setIsBioExpanded] = useState(false);
  const hasGallery = card.galleryImages && card.galleryImages.length > 0;
  const hasVideos = card.youtubeVideos && card.youtubeVideos.length > 0;
  const socialLinks = getSocialLinks(card);
  const extraLinks = getExtraLinks(card);

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-lg mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex flex-col items-center mb-8 pb-8 border-b border-gray-200">
          {/* Profile Image - Large */}
          <div className="relative mb-4">
            {card.profileImage ? (
              <img src={card.profileImage} alt={card.name} className="w-36 h-36 rounded-full object-cover border-2 border-gray-200 shadow-lg" />
            ) : (
              <div className="w-36 h-36 rounded-full bg-gray-900 flex items-center justify-center shadow-lg">
                <span className="text-white text-4xl font-light">{card.name?.charAt(0) || 'U'}</span>
              </div>
            )}
            {isOwner && onUpdateProfileImage && (
              <label htmlFor="profile-upload-minimal" className="absolute bottom-2 right-2 bg-white rounded-full p-1.5 shadow cursor-pointer hover:bg-gray-100 border border-gray-300">
                <Pencil className="w-3.5 h-3.5 text-gray-700" />
                <input id="profile-upload-minimal" type="file" accept="image/*" onChange={onUpdateProfileImage} className="hidden" />
              </label>
            )}
          </div>
          <h1 className="text-2xl font-light text-gray-900 tracking-wide">{card.name}</h1>
          {card.title && <p className="text-gray-500 text-sm mt-1">{card.title}</p>}
          {card.company && <p className="text-gray-400 text-xs">{card.company}</p>}
        </div>

        {/* Contact */}
        <div className="space-y-3 mb-8">
          {card.phone && (
            <a href={`tel:${card.phone}`} onClick={() => trackLinkClick('phone', card.phone)} className="flex items-center gap-3 text-gray-600 hover:text-gray-900 transition-colors">
              <Phone className="w-4 h-4" /><span className="text-sm">{card.phone}</span>
            </a>
          )}
          {card.email && (
            <a href={`mailto:${card.email}`} onClick={() => trackLinkClick('email', card.email)} className="flex items-center gap-3 text-gray-600 hover:text-gray-900 transition-colors">
              <Mail className="w-4 h-4" /><span className="text-sm break-all">{card.email}</span>
            </a>
          )}
          {(card.website || (card.websiteName && card.websiteTLD)) && (
            <a href={card.website || `https://${card.websiteName}${card.websiteTLD}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-gray-600 hover:text-gray-900 transition-colors">
              <Globe className="w-4 h-4" /><span className="text-sm break-all">{(card.website || `${card.websiteName}${card.websiteTLD}`).replace(/^https?:\/\//, '')}</span>
            </a>
          )}
          {(card.location || card.locationAddress) && (
            <a href={
              card.location && (card.location.startsWith('http://') || card.location.startsWith('https://'))
                ? card.location 
                : `https://maps.google.com/?q=${encodeURIComponent(card.locationAddress || card.location || '')}`
            } target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-gray-600 hover:text-gray-900 transition-colors">
              <MapPin className="w-4 h-4" /><span className="text-sm">{card.locationAddress || card.location || 'View Location'}</span>
            </a>
          )}
        </div>

        {/* Social */}
        {socialLinks.length > 0 && (
          <div className="flex flex-wrap gap-3 mb-8 pb-8 border-b border-gray-200">
            {socialLinks.map((social, index) => {
              const BrandIcon = BrandIcons[social.type];
              return (
                <a key={index} href={constructSocialUrl(social.type, social.value)} target="_blank" rel="noopener noreferrer" onClick={() => trackLinkClick(social.type, social.value)} className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center hover:border-gray-400 hover:scale-110 transition-all" title={social.label}>
                  {BrandIcon && <BrandIcon size={18} />}
                </a>
              );
            })}
          </div>
        )}

        {/* Extra Links */}
        {extraLinks.length > 0 && (
          <div className="space-y-2 mb-8">
            {extraLinks.map((link, index) => (
              <a key={index} href={link.url} target="_blank" rel="noopener noreferrer" onClick={() => trackLinkClick('extraLink', link.url)} className="flex items-center gap-3 p-2 border border-gray-200 rounded hover:border-gray-400 transition-colors">
                <ExternalLink className="w-4 h-4 text-gray-500" /><span className="text-gray-700 text-sm">{link.title}</span>
              </a>
            ))}
          </div>
        )}

        {/* Bio */}
        {card.richBio && (
          <div className="mb-8 pb-8 border-b border-gray-200">
            <div className={`overflow-hidden transition-all duration-300 text-sm text-gray-600 ${!isBioExpanded ? 'max-h-12' : 'max-h-[2000px]'}`}>
              <BioRichTextDisplay content={card.richBio} />
            </div>
            <button onClick={() => setIsBioExpanded(!isBioExpanded)} className="flex items-center gap-1 mt-2 text-xs text-gray-500 hover:text-gray-700">
              {isBioExpanded ? <><ChevronUp className="w-3.5 h-3.5" />Show less</> : <><ChevronDown className="w-3.5 h-3.5" />Read more</>}
            </button>
          </div>
        )}

        {/* Gallery */}
        {hasGallery && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-3"><Images className="w-4 h-4 text-gray-500" /><h3 className="text-sm font-medium text-gray-700">Gallery</h3></div>
            <GalleryGrid 
              images={card.galleryImages} 
              onDeleteImage={card.onDeleteImage} 
              isOwner={isOwner}
              accentColor="#1f2937"
            />
          </div>
        )}

        {/* Videos */}
        {hasVideos && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-3"><Play className="w-4 h-4 text-gray-500" /><h3 className="text-sm font-medium text-gray-700">Videos</h3></div>
            <div className="space-y-2">
              {card.youtubeVideos.slice(0, 3).map((videoId, index) => {
                const cleanVideoId = extractYouTubeVideoId(videoId);
                return (
                  <div key={index} className="relative aspect-video rounded overflow-hidden group">
                    <iframe src={`https://www.youtube.com/embed/${cleanVideoId}?rel=0`} title={`Video ${index + 1}`} className="w-full h-full" allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                    {isOwner && card.onDeleteVideo && <button onClick={() => card.onDeleteVideo(index)} className="absolute top-1.5 right-1.5 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10"><Trash2 className="w-3 h-3" /></button>}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BioTemplateMinimal;
